/**
 * Facts stated about a block (spec §7.6.2, M18): `facts[factId]` is a plain `FactEntry` in the block
 * node's data map, beside `links`, `annotations` and `groups`. Quantity kinds and units are QUDT
 * terms; the projector turns each entry into a `km:Fact` node (§13.3).
 */
import { LoroMap, type LoroDoc, type TreeID } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";
import { blocksTree } from "./model";

export interface FactEntry {
  kind: string;
  value: string;
  unit: string | null;
  note: string;
  createdAt: number;
  createdBy: string;
  /** [M31] the ontology class the fact is about, within the block (Q92) */
  about?: string;
  /** [M31] the extraction run a promoted fact came from */
  derivedFrom?: string;
}

export type FactRow = FactEntry & { id: string };

/** Where facts live: a block's node in the page's `blocks` tree, or (M24) a page's node in the
 *  workspace doc's `pages` tree (spec §7.5). The entries are the same either way. */
export type FactTree = "blocks" | "pages";

function factsMap(doc: LoroDoc, treeId: TreeID, create: boolean, tree: FactTree = "blocks"): LoroMap | null {
  const node = (tree === "blocks" ? blocksTree(doc) : doc.getTree("pages")).getNodeByID(treeId);
  if (!node) return null;
  const m = node.data.get("facts");
  if (m instanceof LoroMap) return m;
  return create ? node.data.setContainer("facts", new LoroMap()) : null;
}

/** The block's facts in creation order (UUIDv7 sorts that way). */
export function readFacts(doc: LoroDoc, treeId: TreeID, tree: FactTree = "blocks"): FactRow[] {
  const m = factsMap(doc, treeId, false, tree);
  if (!m) return [];
  const out: FactRow[] = [];
  for (const id of m.keys()) {
    const v = m.get(id);
    if (!v || typeof v !== "object" || v instanceof LoroMap) continue;
    const f = v as Partial<FactEntry>;
    out.push({
      id,
      kind: typeof f.kind === "string" ? f.kind : "",
      value: typeof f.value === "string" ? f.value : "",
      unit: typeof f.unit === "string" && f.unit ? f.unit : null,
      note: typeof f.note === "string" ? f.note : "",
      createdAt: typeof f.createdAt === "number" ? f.createdAt : 0,
      createdBy: typeof f.createdBy === "string" ? f.createdBy : "",
      // [M31] both optional: the class the fact is about (Q92), the run it was promoted from
      ...(typeof f.about === "string" && f.about ? { about: f.about } : {}),
      ...(typeof f.derivedFrom === "string" && f.derivedFrom ? { derivedFrom: f.derivedFrom } : {}),
    });
  }
  return out.sort((a, b) => a.id.localeCompare(b.id));
}

/** Add a fact, or replace one whole (LWW, §7.6.2). Returns its id. */
export function putFact(doc: LoroDoc, treeId: TreeID, userId: string, patch: Partial<FactEntry>, id?: string, tree: FactTree = "blocks"): string {
  const m = factsMap(doc, treeId, true, tree);
  if (!m) throw new Error("node not found");
  const factId = id ?? uuidv7();
  const current = id ? readFacts(doc, treeId, tree).find((f) => f.id === id) : undefined;
  const entry: FactEntry = {
    kind: patch.kind ?? current?.kind ?? "",
    value: patch.value ?? current?.value ?? "",
    unit: patch.unit !== undefined ? patch.unit : (current?.unit ?? null),
    note: patch.note ?? current?.note ?? "",
    createdAt: current?.createdAt ?? Date.now(),
    createdBy: current?.createdBy || userId,
  };
  // [M31] kept across edits; `about` is cleared only by a patch that names it
  const about = "about" in patch ? patch.about : current?.about;
  if (about) entry.about = about;
  if (current?.derivedFrom) entry.derivedFrom = current.derivedFrom;
  m.set(factId, entry);
  doc.commit();
  return factId;
}

export function removeFact(doc: LoroDoc, treeId: TreeID, id: string, tree: FactTree = "blocks"): void {
  factsMap(doc, treeId, false, tree)?.delete(id);
  doc.commit();
}

export const sameFacts = (a: FactRow[], b: FactRow[]): boolean => JSON.stringify(a) === JSON.stringify(b);
